// Steps/FilterSection.js - Step 2 Component
import React, { useContext, useState, useEffect } from 'react';
import { Box, Typography, TextField, Autocomplete, Chip, Checkbox, Alert, CircularProgress, Card, CardContent, Button } from '@mui/material';
import { People, Engineering } from '@mui/icons-material';
import { NotificationDataContext } from '../AddEditNotification';
import AudienceFilter from './AudienceFilter';
import { NotificationServices } from '../NotificationServices';
import { PermissionGuard } from '../../../../components/PermissionGuard';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import NavigationButton from './NavigationButton';
import CSVUploadSection from './CSVUploadSection';
import CSVMatchUpload from './CSVMatchUpload';
import CustomerSelection from './CustomerSelection';
import ExpertSelection from './ExpertSelection';

const FilterSection = () => {
    const { notificationData, setNotificationData,initialState,steps,activeStep,setActiveStep,completedSteps, setCompletedSteps } = useContext(NotificationDataContext);
    const AudienceFilterMethod = useForm({
        mode: 'onChange',
        defaultValues: {
            customerFilters: notificationData.customerFilters || initialState.customerFilters,
            expertFilters: notificationData.expertFilters || initialState.expertFilters
        }
    });
    const { handleSubmit, formState: { isDirty }, control,watch,setValue } = AudienceFilterMethod;
    const completeStep = (data) => {
        console.log('Audience Selection Data:', data);
        setNotificationData(prev => ({
            ...prev,
            customerFilters: data.customerFilters,
            expertFilters: data.expertFilters
        }))
        if (activeStep < steps.length - 1) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
            setCompletedSteps((prev) => {
                prev.add(activeStep);
                return new Set(prev);
            });
        }
    }
    const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
    const [isLoadingExperts, setIsLoadingExperts] = useState(false);
    const [customerList, setCustomerList] = useState([]);
    const [expertList, setExpertList] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    
    const [filters, setFilters] = useState([]);
    const [clearMultiFilter, setClearMultiFilter] = useState(false);
    const [cityList, setCityList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const fetchStateCityLists = async ()=>{
        let cities = [];
        let states = [];
        if (notificationData.audienceType === 'customer') {
            const customerAddresses = await NotificationServices.fetchCustomersAddress();
            if (customerAddresses.success == '1') {
                cities = customerAddresses.data.cities;
                states = customerAddresses.data.states;
            }
        } else if (notificationData.audienceType === 'expert') {
            const expertAddresses = await NotificationServices.fetchExpertsAddress();
            if (expertAddresses.success == '1') {
                cities = expertAddresses.data.cities;
                states = expertAddresses.data.states;
            }
        }
        setCityList(cities);
        setStateList(states);
    }
    const audienceType = watch('audienceType');
    const expertFilters = watch('expertFilters.filterOptions');
    const customerFilters = watch('customerFilters.filterOptions');
    const watchedCustomerFilters = watch('customerFilters');
    const watchedExpertFilters = watch('expertFilters');
    
    useEffect(() => {
        fetchStateCityLists();
    }, [notificationData.audienceType]);

    // Watch form state changes and fetch data automatically
    useEffect(() => {
        if (notificationData.audienceType === 'customer' && customerFilters) {
            fetchCustomerList();
        } else if (notificationData.audienceType === 'expert' && expertFilters) {
            fetchExpertList();
        }
    }, [notificationData.audienceType, customerFilters, expertFilters]);

    // Fetch customer list
    const fetchCustomerList = async () => {
        const formData = AudienceFilterMethod.getValues();
        console.log("fetching with form data:", formData.customerFilters);
        setIsLoadingCustomers(true);
        try {
            const response = await NotificationServices.fetchAllFilteredCustomers(formData.customerFilters,'complete');
            console.log("customer list response:", response);
            if (response.success == '1') {
                setCustomerList(response.data.customers);
            } else if(response.success == '0'){
                setCustomerList([]);
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
        } finally {
            setIsLoadingCustomers(false);
        }
    };

    // Fetch expert list
    const fetchExpertList = async () => {
        const formData = AudienceFilterMethod.getValues();
        console.log("fetching with form data:", formData.expertFilters);
        setIsLoadingExperts(true);
        try {
            const response = await NotificationServices.fetchAllFilteredExperts(formData.expertFilters,'complete');
            console.log("expert list response:", response);
            if (response.success == '1') {
                setExpertList(response.data.experts);
            } else if(response.success == '0'){
                setExpertList([]);
            }
        } catch (error) {
            console.error('Error fetching experts:', error);
        } finally {
            setIsLoadingExperts(false);
        }
    };

    const getFullName = (profile) => {
        if (!profile) return '';
        return [profile.firstName, profile.middleName, profile.lastName]
            .filter(Boolean)
            .join(' ');
    };
    const handleApplyFilter = (filterData) => {
        console.log('Applied Filters:', filterData);
        
        if (notificationData.audienceType === 'customer') {
            // Update form state only - useEffect will trigger fetch automatically
            setValue('customerFilters.filterOptions', filterData.filterOption, { shouldValidate: true });
            setValue('customerFilters.locationFilters', filterData.locationFilters, { shouldValidate: true });
            // Clear selected customers when filters change
            setValue('customerFilters.selectedCustomers', [], { shouldValidate: true });
        } else if (notificationData.audienceType === 'expert') {
            // Update form state only - useEffect will trigger fetch automatically
            setValue('expertFilters.filterOptions', filterData.filterOption, { shouldValidate: true });
            setValue('expertFilters.locationFilters', filterData.locationFilters, { shouldValidate: true });
            // Clear selected experts when filters change
            setValue('expertFilters.selectedExperts', [], { shouldValidate: true });
        }
    };
    const clearFilters = () => {
        setFilters({
            sortKey: 'createdAt',
            sortDirection: 'desc',
            filterOptions: []
        });
        
        setClearMultiFilter(true);
        setTimeout(() => setClearMultiFilter(false), 100);
    };
    
    const [categoryList, setCategoryList] = useState([]);
    
    // Fetch category list on mount
    useEffect(() => {
        // Fetch categories if needed for filters
        // Add your category fetching logic here if required
    }, []);
    // Define filterable columns based on audience type
    const getFilterableColumns = () => {
    if (notificationData.audienceType === 'customer') {
        return [
            // Basic Info
            // {
            //     key: 'name',
            //     label: 'Customer Name',
            //     type: 'text'
            // },
            {
                key: 'mobileNumber',
                label: 'Mobile Number',
                type: 'number'
            },
            // {
            //     key: 'email',
            //     label: 'Email',
            //     type: 'email'
            // },
            
            // Gender - Multi Select
            {
                key: 'gender',
                label: 'Gender',
                type: 'multiselect',
                options: [
                    { value: 'Male', label: 'Male' },
                    { value: 'Female', label: 'Female' },
                    { value: 'Prefer Not To Say', label: 'Prefer Not To Say' }
                ]
            },
            
            // Activity Based Filters
            {
                key: 'lastActive',
                label: 'Last Active',
                type: 'select',
                options: [
                    { value: '7', label: 'Within 7 Days' },
                    { value: '30', label: 'Within 30 Days' },
                    { value: '90', label: 'Within 90 Days' }
                ]
            },
            {
                key: 'lastInactive',
                label: 'Inactive For Days',
                type: 'number',
                placeholder: 'Enter number of days'
            },
            
            // Age Range
            {
                key: 'ageRange',
                label: 'Age Range',
                type: 'range',
                min: 13,
                max: 100,
                defaultValue: [18, 65],
                marks: [
                    { value: 18, label: '18' },
                    { value: 30, label: '30' },
                    { value: 50, label: '50' },
                    { value: 70, label: '70' }
                ]
            },
            
            // Location Filters
            {
                key: 'city',
                label: 'City',
                type: 'multiselect',
                options: cityList.map(city=>({value:city,label:city}))
                // [
                //     { value: 'mumbai', label: 'Mumbai' },
                //     { value: 'delhi', label: 'Delhi' },
                //     { value: 'bangalore', label: 'Bangalore' },
                //     { value: 'pune', label: 'Pune' },
                //     { value: 'hyderabad', label: 'Hyderabad' },
                //     { value: 'chennai', label: 'Chennai' },
                //     { value: 'kolkata', label: 'Kolkata' },
                //     { value: 'ahmedabad', label: 'Ahmedabad' },
                //     { value: 'jaipur', label: 'Jaipur' },
                //     { value: 'lucknow', label: 'Lucknow' }
                //     // Add more cities as needed
                // ]
            },
            {
                key: 'state',
                label: 'State',
                type: 'multiselect',
                options: stateList.map(state=>({value:state,label:state})), 
                // [
                //     { value: 'Maharashtra', label: 'Maharashtra' },
                //     { value: 'Delhi', label: 'Delhi' },
                //     { value: 'Karnataka', label: 'Karnataka' },
                //     { value: 'Tamil nadu', label: 'Tamil Nadu' },
                //     { value: 'West bengal', label: 'West Bengal' },
                //     { value: 'Gujarat', label: 'Gujarat' },
                //     { value: 'Rajasthan', label: 'Rajasthan' },
                //     { value: 'Uttar pradesh', label: 'Uttar Pradesh' },
                //     { value: 'Telangana', label: 'Telangana' },
                //     { value: 'Andhra pradesh', label: 'Andhra Pradesh' }
                //     // Add more states as needed
                // ]
            },
            {
                key: 'pincode',
                label: 'Pincode',
                type: 'number',
                placeholder: 'Enter pincode'
            },
            
            // Order Based Filters
            {
                key: 'noOrdersSince',
                label: 'No Orders Since',
                type: 'select',
                options: [
                    { value: '30', label: 'Last 30 Days' },
                    { value: '90', label: 'Last 90 Days' },
                    { value: '120', label: 'Last 120 Days' },
                    { value: '180', label: 'Last 180 Days' }
                ]
            },
            
            // Category Filters
            {
                key: 'orderCategory',
                label: 'Ordered Category',
                type: 'multiselect',
                options: categoryList.map(cat=>({value:cat.id,label:cat.title}))
            },
        ];
    } else {
        // Expert filters
        return [
            // Basic Info
            // {
            //     key: 'providerName',
            //     label: 'Expert Name',
            //     type: 'text'
            // },
            // {
            //     key: 'add_mobileNumber',
            //     label: 'Mobile Number',
            //     type: 'number'
            // },
            // {
            //     key: 'email',
            //     label: 'Email',
            //     type: 'email'
            // },
            
            // Specialty
            // {
            //     key: 'specialty',
            //     label: 'Specialty',
            //     type: 'multiselect',
            //     options: [
            //         { value: 'cardiology', label: 'Cardiology' },
            //         { value: 'dermatology', label: 'Dermatology' },
            //         { value: 'neurology', label: 'Neurology' },
            //         { value: 'pediatrics', label: 'Pediatrics' },
            //         { value: 'orthopedics', label: 'Orthopedics' },
            //         { value: 'gynecology', label: 'Gynecology' },
            //         { value: 'psychiatry', label: 'Psychiatry' },
            //         { value: 'dentistry', label: 'Dentistry' }
            //         // Add more specialties as needed
            //     ]
            // },
            
            // Activity Based
            {
                key: 'lastActive',
                label: 'Last Active',
                type: 'select',
                options: [
                    { value: '7', label: 'Within 7 Days' },
                    { value: '30', label: 'Within 30 Days' },
                    { value: '90', label: 'Within 90 Days' }
                ]
            },
            
            // Age Range
            {
                key: 'ageRange',
                label: 'Age Range',
                type: 'range',
                min: 13,
                max: 100,
                defaultValue: [18, 65],
                marks: [
                    { value: 18, label: '18' },
                    { value: 30, label: '30' },
                    { value: 50, label: '50' },
                    { value: 70, label: '70' }
                ]
            },
            
            // Gender
            {
                key: 'gender',
                label: 'Gender',
                type: 'multiselect',
                options: [
                    { value: 'Male', label: 'Male' },
                    { value: 'Female', label: 'Female' },
                    { value: 'Prefer Not To Say', label: 'Prefer Not To Say' }
                ]
            },
            
            // Location Filters
            {
                key: 'city',
                label: 'City',
                type: 'multiselect',
                options: cityList.map(city=>({value:city,label:city}))
                // [
                //     { value: 'mumbai', label: 'Mumbai' },
                //     { value: 'delhi', label: 'Delhi' },
                //     { value: 'bangalore', label: 'Bangalore' },
                //     { value: 'pune', label: 'Pune' },
                //     { value: 'hyderabad', label: 'Hyderabad' },
                //     { value: 'chennai', label: 'Chennai' },
                //     { value: 'kolkata', label: 'Kolkata' },
                //     { value: 'ahmedabad', label: 'Ahmedabad' },
                //     { value: 'jaipur', label: 'Jaipur' },
                //     { value: 'lucknow', label: 'Lucknow' }
                //     // Add more cities as needed
                // ]
            },
            {
                key: 'state',
                label: 'State',
                type: 'multiselect',
                options: stateList.map(state=>({value:state,label:state})), 
                // [
                //     { value: 'Maharashtra', label: 'Maharashtra' },
                //     { value: 'Delhi', label: 'Delhi' },
                //     { value: 'Karnataka', label: 'Karnataka' },
                //     { value: 'Tamil nadu', label: 'Tamil Nadu' },
                //     { value: 'West bengal', label: 'West Bengal' },
                //     { value: 'Gujarat', label: 'Gujarat' },
                //     { value: 'Rajasthan', label: 'Rajasthan' },
                //     { value: 'Uttar pradesh', label: 'Uttar Pradesh' },
                //     { value: 'Telangana', label: 'Telangana' },
                //     { value: 'Andhra pradesh', label: 'Andhra Pradesh' }
                //     // Add more states as needed
                // ]
            },
            {
                key: 'pincode',
                label: 'Pincode',
                type: 'number',
                placeholder: 'Enter pincode'
            },
            
            // Service Status
            // {
            //     key: 'status',
            //     label: 'Status',
            //     type: 'select',
            //     options: [
            //         { value: 'active', label: 'Active' },
            //         { value: 'inactive', label: 'Inactive' }
            //     ]
            // },
            
            // Booking Based Filters
            {
                key: 'bookingsReceivedInDays',
                label: 'Bookings Received in Last X Days',
                type: 'number',
                placeholder: 'Enter number of days'
            },
            {
                key: 'noBookingsSince',
                label: 'No Bookings Since Days',
                type: 'number',
                placeholder: 'Days without bookings'
            },
        ];
    }
    };

    if (!notificationData.audienceType) {
        return (
            <Alert severity="warning" sx={{ mt: 2 }}>
                Please select an audience type first.
            </Alert>
        );
    }
    
    // If CSV upload is selected, show CSV upload component
    if (notificationData.audienceType === 'csvUpload') {
        return <CSVUploadSection />;
    }
    
    

    // Expert Selection Component
    
    return (
        <>
            <Card sx={{borderBottomLeftRadius:0,borderBottomRightRadius:0}}>
                <CardContent sx={{ height:'max-content',paddingBottom: `0!important` }}>
                    {/* Enhanced AudienceFilter with geo filtering */}
                    <Box sx={{ mb: 3,display:'flex',alignItems:'center',justifyContent:'space-between' }}>
                        <AudienceFilter
                            key={`${notificationData.audienceType}-${JSON.stringify(notificationData.audienceType === 'customer' ? watchedCustomerFilters.filterOptions : watchedExpertFilters.filterOptions)}`}
                            filterableColumns={getFilterableColumns()}
                            defaultFilterOptions= {notificationData.audienceType === 'customer' ? watchedCustomerFilters.filterOptions : watchedExpertFilters.filterOptions}
                            onApplyFilter={handleApplyFilter}
                            externalClear={clearMultiFilter}
                            // enableGeoFilters={notificationData.audienceType === 'customer' ? true : false}
                            enableGeoFilters={false}
                            enableLocationFilters={true}
                            defaultLocationFilters= {notificationData.audienceType === 'customer' ? watchedCustomerFilters.locationFilters : watchedExpertFilters.locationFilters}
                            // defaultLocationFilters={{
                            //     locationFilter: 'radius',
                            //     centerCoordinates: { lat: '', lng: '' },
                            //     radius: '',
                            //     polygon: []
                            // }}
                            audienceType= {notificationData.audienceType === 'customer' ? 'customer' : 'expert'}
                        />
                        {/* loading states and feedback */}
                        {(isLoadingCustomers) ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2">
                                    Applying filters...
                                </Typography>
                            </Box>
                        ):
                        // (notificationData.customerFilters.filterOptions.length>0 ||  notificationData.expertFilters.filterOptions.length>0 )&&
                            (
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                {notificationData.audienceType === 'customer' ? <People fontSize="small" /> : <Engineering fontSize="small" />}
                                Filtered Results: {(isLoadingCustomers || isLoadingExperts) ? <CircularProgress size={16} sx={{ ml: 1 }} /> :notificationData.audienceType === 'customer' ? customerList.length : expertList.length} {notificationData.audienceType}(s)
                            </Typography>
                            )
                        }
                    </Box>
                </CardContent>
            </Card>
            <FormProvider {...AudienceFilterMethod}>
                <Box component={'form'} onSubmit={handleSubmit(completeStep)}>
                    <Card>
                        <CardContent sx={{ height:'max-content' }}>
                            {/* Detailed Selection Component */}
                            {notificationData.audienceType === 'customer' ? (
                                <CustomerSelection
                                    filteredData={customerList}
                                    isLoading={isLoadingCustomers}
                                    getFullName={getFullName}
                                    notificationData={notificationData}
                                    setNotificationData={setNotificationData}
                                />
                            ) : (
                                <ExpertSelection
                                    filteredData={expertList}
                                    isLoading={isLoadingExperts}
                                    getFullName={getFullName}
                                    notificationData={notificationData}
                                    setNotificationData={setNotificationData}
                                />
                            )}
                        </CardContent>
                    </Card>
                    {/* Navigation Buttons */}
                    <NavigationButton isDirty={isDirty} />
                </Box>
            </FormProvider>
        </>
    );
};

// Customer Selection Component


export default FilterSection;